"use strict";

const {createReporter} = require("./clientDiagnostics.core");

function createBrowserDiagnostics({clientApp, clientMode, release, getEndpoint,
  getToken = () => null, enabled = true, getWindow = () =>
    typeof window === "undefined" ? null : window}) {
  let installedOn = null;
  let removeListeners = null;
  const report = createReporter(async payload => {
    const browser = getWindow();
    if (!enabled || !browser?.fetch) return;
    // A reporting request never uses the observed Axios instance.
    const controller = typeof browser.AbortController === "function" ?
      new browser.AbortController() : null;
    const timeout = setTimeout(() => controller?.abort(), 5000);
    let tokenTimer;
    try {
      const token = await Promise.race([
        Promise.resolve().then(getToken).catch(() => null),
        new Promise(resolve => { tokenTimer = setTimeout(() => resolve(null), 1000); }),
      ]);
      clearTimeout(tokenTimer);
      const endpoint = getEndpoint();
      if (!endpoint) return;
      await browser.fetch(endpoint, {
        method: "POST",
        headers: {"Content-Type": "application/json",
          ...(token ? {Authorization: "Bearer " + token} : {})},
        body: JSON.stringify(payload),
        credentials: "omit",
        ...(controller ? {signal: controller.signal} : {}),
        keepalive: true,
      });
    } catch (_) {
      // Offline, blocked, rate-limited or unavailable: no recursive report/retry.
    } finally {
      clearTimeout(timeout);
      clearTimeout(tokenTimer);
    }
  }, {clientApp, platform: "web", clientMode, release, kind: "handled"});

  const init = () => {
    const browser = getWindow();
    if (!enabled || !browser?.addEventListener || installedOn === browser) return;
    removeListeners?.();
    const onError = event => {
      // Resource-load events can contain signed image URLs; only JS ErrorEvents.
      if (event?.error) report(event.error, "window.error", {kind: "unhandled"});
    };
    const onRejection = event => report(event?.reason, "window.promise", {kind: "unhandled_promise"});
    browser.addEventListener("error", onError);
    browser.addEventListener("unhandledrejection", onRejection);
    installedOn = browser;
    removeListeners = () => {
      browser.removeEventListener("error", onError);
      browser.removeEventListener("unhandledrejection", onRejection);
      installedOn = null;
    };
  };
  return {report, init, dispose: () => { removeListeners?.(); removeListeners = null; }};
}

module.exports = {createBrowserDiagnostics};
